import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CassRelatedAccount } from '../../../data/cass-accounts.service';
import { getCassAccountTypeLabel } from './cass-account-type-cell.component';

export interface ICassAccountTypeChangeData {
  accountNumber: string;
  vendorName: string;
  oldType: string;
  newType: string;
  relatedType: string;
  loadRelatedAccounts: () => Observable<CassRelatedAccount[]>;
  confirmRequest: (relatedAccounts: CassRelatedAccount[]) => Observable<any>;
}

@Component({
  selector: 'exa-cass-account-type-change-dialog',
  template: `
    <h1 mat-dialog-title fxLayout="row" fxLayoutAlign="start center">
      <mat-icon class="align--middle margin--right">sync_problem</mat-icon>
      <span>Change Account Type</span>
    </h1>
    <div mat-dialog-content>
      <p>
        You are changing the type of account <strong>{{data.accountNumber}}</strong>
        <span *ngIf="data.vendorName"> ({{data.vendorName}})</span>
        from <strong>{{getLabel(data.oldType)}}</strong> to <strong>{{getLabel(data.newType)}}</strong>.
      </p>

      <div *ngIf="loadingRelated" class="related__loading">
        <mat-spinner diameter="24"></mat-spinner>
        <span>Loading related accounts...</span>
      </div>
      <p *ngIf="loadError" class="text--warn">Related accounts could not be loaded, please try again.</p>

      <ng-container *ngIf="relatedAccounts">
        <ng-container *ngIf="relatedAccounts.length; else noRelated">
          <p>The following related accounts will be changed to <strong>{{getLabel(data.relatedType)}}</strong>:</p>
          <table class="related__table">
            <thead>
              <tr>
                <th>Account Number</th>
                <th>Account Name</th>
                <th>Vendor Name</th>
                <th>Old Type</th>
                <th>New Type</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let relatedAccount of relatedAccounts">
                <td>{{relatedAccount.account_number}}</td>
                <td>{{relatedAccount.account_name}}</td>
                <td>{{relatedAccount.vendor_name}}</td>
                <td>{{getLabel(relatedAccount.type)}}</td>
                <td><strong>{{getLabel(data.relatedType)}}</strong></td>
              </tr>
            </tbody>
          </table>
        </ng-container>
        <ng-template #noRelated>
          <p>This account has no active related accounts.</p>
        </ng-template>

        <p>
          The bills of this account<span *ngIf="relatedAccounts.length"> and its related accounts</span>
          will be regenerated with the new types.
        </p>
      </ng-container>

      <p *ngIf="error" class="text--warn">Something went wrong, please try again.</p>
    </div>
    <div mat-dialog-actions class="type-change__actions">
      <button mat-button class="type-change__button" (click)="onCancel()" [disabled]="loading">Cancel</button>
      <button mat-raised-button class="type-change__button" color="primary" (click)="onRegenerate()" [disabled]="loading || !relatedAccounts">
        Regenerate Bills
      </button>
    </div>
  `,
  styles: [`
    .related__loading {
      display: flex;
      align-items: center;
      margin: 16px 0;
      color: #757575;
    }

    .related__loading span {
      margin-left: 12px;
    }

    .related__table {
      width: 100%;
      margin-bottom: 16px;
      border-collapse: collapse;
      font-size: 13px;
    }

    .related__table th,
    .related__table td {
      padding: 6px 8px;
      border-bottom: 1px solid #e0e0e0;
      text-align: left;
    }

    .related__table th {
      font-weight: 500;
      color: #757575;
    }

    .type-change__actions {
      display: flex;
      justify-content: flex-end;
    }

    .type-change__button {
      flex-shrink: 0;
      min-width: 100px;
      padding: 0 16px;
      white-space: nowrap;
    }

    .type-change__button + .type-change__button {
      margin-left: 8px;
    }
  `],
})
export class CassAccountTypeChangeDialogComponent implements OnInit {
  loading = false;
  error = false;
  loadingRelated = false;
  loadError = false;
  relatedAccounts: CassRelatedAccount[];
  getLabel = getCassAccountTypeLabel;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ICassAccountTypeChangeData,
    public dialogRef: MatDialogRef<CassAccountTypeChangeDialogComponent>,
    private toastr: ToastrService,
  ) {
    // closing only through the buttons, so a running request can't be dismissed halfway
    dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.loadingRelated = true;

    this.data.loadRelatedAccounts()
      .pipe(finalize(() => this.loadingRelated = false))
      .subscribe(
        relatedAccounts => this.relatedAccounts = relatedAccounts,
        () => this.loadError = true,
      );
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  onRegenerate() {
    this.loading = true;
    this.error = false;

    this.data.confirmRequest(this.relatedAccounts)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        () => {
          this.toastr.success(
            `Account ${this.data.accountNumber} type changed to ${this.getLabel(this.data.newType)}. ` +
            'Bills regeneration started, this will take a few minutes.',
          );
          this.dialogRef.close(true);
        },
        () => this.error = true,
      );
  }
}
